/**
 * Rig-avatar resolve chain (E2.4.S1) — turn the player rig pack + a character's
 * `CharacterAppearance` into the asset-pack + models the avatar builder should
 * instantiate. Self/other-agnostic: appearance is a full-state field on both the
 * self (`helloAck.you`) and remote players (AD-137), so one resolve path serves
 * every player entity the client can see.
 *
 *   RIG_PACK_ID  (frontend-local constant, convention a)
 *     → client-pack asset-packs[RIG_PACK_ID]      (the AssetPackDef manifest)
 *       → AssetLoader.instantiate(RIG_BODY_MODEL, lod0)   (done by the builder)
 *
 * Wardrobe (convention c): each `CharacterAppearance` face/hair/skin option id is
 * looked up in the SAME rig pack manifest — first by the option id, then by the
 * option's `assetRef` register-family as a fallback key (appearance-customisation.md
 * §2: `assetRef` "resolved at AP4 rig integration"). Found ⇒ an attachment the
 * builder instantiates at the head/body attach; absent ⇒ a no-op that leaves the
 * default look (spec §8 graceful-fallback: an unknown id never breaks rendering).
 *
 * Every unresolvable link returns `null`/`[]` so the caller falls back gracefully:
 * NO rig pack ⇒ `resolveRig` returns `null` and the caller keeps the existing
 * capsule+nose placeholder (the never-blank-world rule, AD-129 / spec §8).
 *
 * FRONTEND-LOCAL by design (conventions a + c): the pack-id constant and the
 * wardrobe id→assetRef resolution live here, never in `shared/` — promoting either
 * is a T3 DR escalation out of the feature (the AD-141 `RESOURCE_NODE_ASSET` /
 * E2.2.S3 gather-tool-map precedent). Pure + node-safe (types only from the client
 * surface). The wardrobe map is frontend-local, not content, so `validate.ts`'s
 * AD-413 `presentation.model` membership rule never sees it — an out-of-pack id
 * degrades to no-attachment at the loader. Import boundary (AD-122): shared types via `@great-cloud/shared/client`.
 */

import { APPEARANCE_SLOTS } from '@great-cloud/shared/client';
import type {
  AppearanceOptionDef,
  AppearanceSlot,
  AssetPackDef,
  CharacterAppearance,
} from '@great-cloud/shared/client';

/**
 * Convention (a): the humanoid rig pack id (CHR-026 §6 naming) + its base body
 * entry id. FRONTEND-LOCAL — the client's resolution convention, not a `shared/`
 * constant (promotion = T3). The committed `player-rig-v1` pack (AD-181 interim
 * rig) carries `body-base`; a real rig arriving under a different entry id is a
 * re-import-time reconciliation (recorded on the E2.5/E4.4 checklist), not a
 * silhouette this story hardcodes elsewhere.
 */
export const RIG_PACK_ID = 'player-rig-v1';
export const RIG_BODY_MODEL = 'body-base';
/**
 * Clips-only packs on the rig's own skeleton, loaded beside the body and appended to its
 * clip list (AD-670): the library attack/combo clips (AD-9008) ship as `attack-combos`
 * because merged into `body-base` they would break the 4 MiB mobile budget. Same
 * frontend-local convention as `RIG_BODY_MODEL`; a pack the manifest does not list is
 * simply skipped. `ability-clips` carries the per-ability player clips (actionClips.ts
 * `ABILITY_CLIPS`; q-stamina-bar-and-ability-animations), library windows prepared the same way.
 */
export const RIG_CLIP_PACK_MODELS: readonly string[] = ['attack-combos', 'ability-clips'];
/**
 * The low tier's first-frame body (q-mobile-first-frame-clip-split): the same mesh, skin and
 * skeleton as `body-base`, carrying only the clips the generic maps resolve to and the morphs
 * the client drives; every other body clip ships clips-only as `body-actions`. Both derive
 * from `body-base.glb` (tools/asset-compress/lean-rig-body.mjs). Used only as a pair: a pack
 * missing either keeps the full body.
 */
export const RIG_LEAN_BODY_MODEL = 'body-base-lean';
export const RIG_BODY_ACTIONS_MODEL = 'body-actions';

/** Whether a rig pack delivers the lean body AND its action clips (both, or neither is used). */
export function packHasLeanBody(pack: AssetPackDef): boolean {
  return packHasAsset(pack, RIG_LEAN_BODY_MODEL) && packHasAsset(pack, RIG_BODY_ACTIONS_MODEL);
}

/**
 * How the builder reads rig presentation data — the client content pack's
 * `asset-packs` projection plus the `appearance-options` catalogue (for the
 * `assetRef` fallback key). Built in App from the loaded `ContentPackStore`
 * (`get('asset-packs', id)` / `get('appearance-options', id)`); a small test fake
 * satisfies it too. Kept separate from `EquipmentContent`/`CreatureContent` so this
 * feature touches no other module's contract.
 */
export interface RigContent {
  /** Client-pack `asset-packs[id]` (its `assets[]` manifest), or undefined if not delivered. */
  assetPack(id: string): AssetPackDef | undefined;
  /** Client-pack `appearance-options[id]` (for its `assetRef` family), or undefined. */
  appearanceOption(id: string): AppearanceOptionDef | undefined;
}

/** A resolved rig: the pack + the base body model id the builder instantiates. */
export interface RigResolution {
  /** The rig pack id (`RIG_PACK_ID`). */
  packId: string;
  /** The base body asset id to instantiate at lod0 (`RIG_BODY_MODEL`). */
  bodyModel: string;
  /** The resolved pack def — construct an `AssetManifest`/`AssetLoader` from it. */
  pack: AssetPackDef;
}

/** Where a resolved wardrobe piece attaches: face/hair on the head, skin on the body. */
export type WardrobeAttach = 'head' | 'body';

/** A resolved wardrobe attachment: the asset the builder instantiates + where it goes. */
export interface WardrobeResolution {
  /** The appearance slot this satisfies (`face` | `hair` | `skin`). */
  slot: AppearanceSlot;
  /** The chosen appearance-option id (`CharacterAppearance[slot]`). */
  optionId: string;
  /** The rig-pack asset id resolved for it (the option id, or its `assetRef` family). */
  assetId: string;
  /** The attach point: head for face/hair, body for skin (a material swap in v1 — recorded). */
  attach: WardrobeAttach;
}

/** face/hair attach at the head node; skin is a body-material concern (no head mesh). */
const WARDROBE_ATTACH: Readonly<Record<AppearanceSlot, WardrobeAttach>> = {
  face: 'head',
  hair: 'head',
  skin: 'body',
};

/** Whether a pack delivers an asset id (membership is a client convention, never a validator semantic). */
function packHasAsset(pack: AssetPackDef, id: string): boolean {
  return (pack.assets ?? []).some((asset) => asset.id === id);
}

/**
 * Resolve the player rig, or `null` when the rig pack is not delivered (⇒ the
 * caller keeps the capsule+nose placeholder — the graceful capsule fallback). The
 * final `instantiate(RIG_BODY_MODEL, lod0)` — and its own null fallback for an
 * unknown model / load error — is the builder's/loader's job.
 */
export function resolveRig(content: Pick<RigContent, 'assetPack'>): RigResolution | null {
  const pack = content.assetPack(RIG_PACK_ID);
  if (pack === undefined) return null; // rig pack not delivered ⇒ capsule fallback
  return { packId: RIG_PACK_ID, bodyModel: RIG_BODY_MODEL, pack };
}

/**
 * Resolve a character's wardrobe (face/hair/skin option ids) to the rig-pack assets
 * to attach, per convention (c): each option id is looked up in the rig pack — first
 * directly, then by the option's `assetRef` register-family as a fallback key. An
 * option with no matching asset is simply omitted (a no-op default look, spec §8) —
 * never an error. Skin resolves the same way but is a body-material concern (v1's
 * interim rig is untextured, so skin typically no-ops; recorded).
 */
export function resolveWardrobe(
  content: Pick<RigContent, 'appearanceOption'>,
  appearance: CharacterAppearance,
  pack: AssetPackDef,
): WardrobeResolution[] {
  const out: WardrobeResolution[] = [];
  for (const slot of APPEARANCE_SLOTS) {
    const optionId = appearance[slot];
    if (optionId === undefined || optionId === '') continue;
    // (c) direct id first, then the option's assetRef family, both in the rig pack.
    let assetId: string | undefined;
    if (packHasAsset(pack, optionId)) {
      assetId = optionId;
    } else {
      const family = content.appearanceOption(optionId)?.assetRef;
      if (family !== undefined && family !== '' && packHasAsset(pack, family)) assetId = family;
    }
    if (assetId === undefined) continue; // absent in the pack ⇒ no-op (default look, spec §8)
    out.push({ slot, optionId, assetId, attach: WARDROBE_ATTACH[slot] });
  }
  return out;
}
